import React from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getRuntimeCapabilities } from '../../api/runtime/capabilities';
import { getGrpcPort } from '../../api/grpc/transport';
import { pipeexRuntimeConfig } from '../../config/pipeexRuntimeConfig';
import { useAuthStore } from '../../stores/useAuthStore';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { borderRadius, layoutGap } from '../../../theme/theme';

export default function IntegrationDiagnosticsPanel() {
  const theme = useTheme();
  const runtime = getRuntimeCapabilities();
  const grpcPort = getGrpcPort();
  const {
    user,
    accessToken,
    refreshToken,
    machineFingerprint,
    fingerprintLoading,
    fingerprintError,
    hydrated,
  } = useAuthStore();
  const { lastApiError, lastGrpcError } = useIntegrationDiagnosticsStore();

  const items = [
    `mode=${runtime.mode}`,
    `grpcPort=${grpcPort ?? 'n/a'}`,
    `api=${pipeexRuntimeConfig.apiBaseUrl}`,
    `mock=${pipeexRuntimeConfig.useMockData ? 'on' : 'off'}`,
    `skipAuthMock=${pipeexRuntimeConfig.skipAuthInMockMode ? 'on' : 'off'}`,
    `authHydrated=${hydrated ? 'yes' : 'no'}`,
    `user=${user?.username || 'none'}`,
    `token=${accessToken ? 'yes' : 'no'}`,
    `refresh=${refreshToken ? 'yes' : 'no'}`,
    `fp=${machineFingerprint ? 'yes' : 'no'}`,
    `fpLoading=${fingerprintLoading ? 'yes' : 'no'}`,
    `fpError=${fingerprintError ? 'yes' : 'no'}`,
    `lastApiErr=${lastApiError ? 'yes' : 'no'}`,
    `lastGrpcErr=${lastGrpcError ? 'yes' : 'no'}`,
  ];

  return (
    <Box
      component="aside"
      aria-label="Integration diagnostics"
      aria-live="polite"
      data-testid="integration-diagnostics-panel"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: layoutGap.row,
        px: 1,
        pb: 0.75,
        minWidth: 0,
        overflow: 'hidden',
        backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.08 : 0.12),
        borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.24)}`,
      }}
    >
      {items.map((item) => (
        <Box
          key={item}
          sx={{
            px: 0.75,
            py: layoutGap.micro,
            fontSize: '0.65rem',
            lineHeight: 1.2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.28)}`,
            borderRadius: `${borderRadius.sm}px`,
            color: theme.palette.info.main,
            backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.08 : 0.14),
            whiteSpace: 'nowrap',
          }}
        >
          {item}
        </Box>
      ))}
      {lastApiError && (
        <Box
          sx={{
            px: 0.75,
            py: layoutGap.micro,
            fontSize: '0.65rem',
            lineHeight: 1.2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.34)}`,
            borderRadius: `${borderRadius.sm}px`,
            color: theme.palette.error.main,
            backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'light' ? 0.08 : 0.14),
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={lastApiError}
        >
          API: {lastApiError}
        </Box>
      )}
      {lastGrpcError && (
        <Box
          sx={{
            px: 0.75,
            py: layoutGap.micro,
            fontSize: '0.65rem',
            lineHeight: 1.2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.34)}`,
            borderRadius: `${borderRadius.sm}px`,
            color: theme.palette.warning.main,
            backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.08 : 0.14),
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={lastGrpcError}
        >
          gRPC: {lastGrpcError}
        </Box>
      )}
    </Box>
  );
}
